import { storage } from 'utils/typedStorage';
import env from 'env';
import { useSupportUserStore } from 'stores/supportUserStore';
import { isSupportLoginEmail } from 'utils/supportUsers';

type PendoInitializeArgs = {
  visitor: {
    id: string;
    email?: string;
    full_name?: string;
    role?: string;
    // role_key?: string;
    isSupport?: boolean;
    organization_id?: string;
    deploy_target?: 'local' | 'deployed';
    app_env?: 'development' | 'production';
    environment?: string;
    [key: string]: unknown;
  };
  account: {
    id: string;
    name?: string;
    [key: string]: unknown;
  };
};

type PendoAgent = {
  initialize?: (args: PendoInitializeArgs) => void;
  identify?: (args: PendoInitializeArgs) => void;
  pageLoad?: () => void;
  updateOptions?: (opts: Record<string, unknown>) => void;
  track?: (eventName: string, properties?: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    // The Pendo snippet installs `window.pendo` (a queuing stub) before the agent loads.
    pendo?: PendoAgent;
  }
}

function getPendo(): PendoAgent | null {
  // The snippet adds `window.pendo` (with a queuing stub) before the agent loads.
  // In case it isn't present for some reason, we no-op.
  if (typeof window === 'undefined') return null;
  return window.pendo ?? null;
}

function readLocalStorageString(key: string): string | undefined {
  const val = storage.getDynamic(key);
  if (!val) return undefined;
  const trimmed = val.trim();
  return trimmed.length ? trimmed : undefined;
}

function buildPendoArgsFromLocalStorage(): PendoInitializeArgs | null {
  // Source of truth in this app is localStorage (populated by setLocalStorageFromEmail)
  const userId = readLocalStorageString('userId');
  const orgId = readLocalStorageString('orgId');
  if (!userId || !orgId) return null;

  const email = readLocalStorageString('email');
  const firstName = readLocalStorageString('firstName');
  const lastName = readLocalStorageString('lastName');
  const orgName = readLocalStorageString('orgName');
  const fullName =
    firstName || lastName
      ? `${firstName ?? ''} ${lastName ?? ''}`.trim()
      : undefined;

  // Check support user status from store (synced with localStorage)
  // Note: support user flows can be special; we still allow initialization if IDs exist.
  const isSupport =
    useSupportUserStore.getState().isSupportUser || isSupportLoginEmail(email);

  return {
    visitor: {
      id: userId,
      email,
      full_name: fullName,
      isSupport,
      organization_id: orgId,
      deploy_target: env.IS_LOCAL ? 'local' : 'deployed',
      app_env: env.ENV === 'development' ? 'development' : 'production',
      environment: env.REACT_APP_ENVIRONMENT,
    },
    account: {
      id: orgId,
      name: orgName,
    },
  };
}

let lastInitKey: string | null = null;

/**
 * Initialize Pendo using localStorage as the source of truth.
 * Returns true if initialized (or already initialized with the same identity), false if missing data.
 */
export function pendoInitializeFromLocalStorage(): boolean {
  const pendo = getPendo();
  const args = buildPendoArgsFromLocalStorage();
  if (!pendo || !args) return false;

  const initKey = `${args.visitor.id}::${args.visitor.organization_id ?? ''}`;
  if (lastInitKey === initKey) return true;

  // `initialize` is the canonical call; `identify` also exists. Using initialize
  // keeps behavior simple for first-load and refreshes.
  pendo.initialize?.(args);
  lastInitKey = initKey;
  return true;
}

/**
 * For single-page apps, call this on route changes so Pendo registers a "page view".
 */
export function pendoPageLoad(): void {
  const pendo = getPendo();
  pendo?.pageLoad?.();
}
